import { createClient } from "@supabase/supabase-js";
import { Database } from "@/types/database";
import { cookieStorage } from "@/lib/cookie-storage";

/**
 * Dual Supabase client configuration for Agency-Auth SSO integration.
 *
 * ARCHITECTURE:
 * - authClient: Handles authentication via Agency-Auth SSO (vahmixxcyudkjktbcivw)
 * - docsClient: Handles document/export/collection operations (xbgjuttuutyyrkpcjiet)
 *
 * Required environment variables:
 * - VITE_AUTH_SUPABASE_URL: Agency-Auth SSO database URL
 * - VITE_AUTH_SUPABASE_ANON_KEY: Agency-Auth anon key
 * - VITE_DOCS_SUPABASE_URL: LocalDocs documents database URL
 * - VITE_DOCS_SUPABASE_ANON_KEY: LocalDocs anon key
 *
 * Set these in your .env file or deployment platform (Vercel, etc.)
 */

const AUTH_SUPABASE_URL = import.meta.env.VITE_AUTH_SUPABASE_URL;
const AUTH_SUPABASE_ANON_KEY = import.meta.env.VITE_AUTH_SUPABASE_ANON_KEY;
const DOCS_SUPABASE_URL = import.meta.env.VITE_DOCS_SUPABASE_URL;
const DOCS_SUPABASE_ANON_KEY = import.meta.env.VITE_DOCS_SUPABASE_ANON_KEY;

// Runtime validation - fail fast with actionable error message
if (!AUTH_SUPABASE_URL || !AUTH_SUPABASE_ANON_KEY) {
  throw new Error(
    "Missing Agency-Auth configuration. Please set the following environment variables:\n" +
      "  - VITE_AUTH_SUPABASE_URL=https://vahmixxcyudkjktbcivw.supabase.co\n" +
      "  - VITE_AUTH_SUPABASE_ANON_KEY=your_auth_anon_key_here\n\n" +
      "Add these to your .env file or configure them in your deployment platform.\n" +
      "See .env.example for the complete configuration template."
  );
}

if (!DOCS_SUPABASE_URL || !DOCS_SUPABASE_ANON_KEY) {
  throw new Error(
    "Missing LocalDocs configuration. Please set the following environment variables:\n" +
      "  - VITE_DOCS_SUPABASE_URL=https://xbgjuttuutyyrkpcjiet.supabase.co\n" +
      "  - VITE_DOCS_SUPABASE_ANON_KEY=your_docs_anon_key_here\n\n" +
      "Add these to your .env file or configure them in your deployment platform.\n" +
      "See .env.example for the complete configuration template."
  );
}

/**
 * Auth client for Agency-Auth SSO.
 * Use for: login, signup, password reset, session management, user profiles
 *
 * CROSS-DOMAIN SSO: Uses cookie storage with domain=.projectassistant.ai
 * This enables SSO across all subdomains (localdocs, nexus, etc.)
 */
export const authClient = createClient(AUTH_SUPABASE_URL, AUTH_SUPABASE_ANON_KEY, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    flowType: "pkce",
    storage: cookieStorage,
    storageKey: "sb-auth-token", // Shared key across all projectassistant.ai apps
  },
});

/**
 * Docs client for LocalDocs business data.
 * Use for: documents, exports, collections, tags, maintenance logs
 *
 * NOTE: Auth is disabled for docsClient (uses anon key only).
 * RLS policies on xbgjuttuutyyrkpcjiet allow anon access (team-shared model).
 * Authentication is handled by authClient (Agency-Auth).
 */
export const docsClient = createClient<Database>(DOCS_SUPABASE_URL, DOCS_SUPABASE_ANON_KEY, {
  auth: {
    persistSession: false,
    autoRefreshToken: false,
    detectSessionInUrl: false,
    storageKey: "localdocs-docs", // Unique key to avoid conflicts with authClient
  },
});

/**
 * Legacy export for backwards compatibility.
 * @deprecated Use authClient for auth operations and docsClient for data operations.
 * This will be removed in a future version.
 */
export const supabase = authClient;

/**
 * NOTE: docsClient uses anon key for all operations.
 *
 * With the dual-database architecture:
 * - authClient (Agency-Auth) handles user authentication
 * - docsClient (LocalDocs) stores business data with anon RLS policies
 *
 * Frontend ProtectedRoute ensures only authenticated users access the app.
 * LocalDocs database RLS policies allow anon access (team-shared model).
 */
